import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Download, FilterX } from 'lucide-react';
import {
  exportAuditLogs,
  getAuditLogs,
  type AuditLogEntry,
  type ProtokollFilter,
} from '@/api/auditLog.api';

// Die Schlüssel müssen mit denen im Server übereinstimmen (logAction). Sie
// standen hier einmal anders – 'user.login' statt 'login.success' – und dann
// filterte die Auswahl auf Aktionen, die es nicht gibt: die Liste blieb leer,
// und in der Tabelle stand der Rohwert ohne Beschriftung.
const ACTION_LABELS: Record<string, string> = {
  'login.success': 'Anmeldung',
  'login.failed': 'Anmeldung fehlgeschlagen',
  'login.blocked': 'Anmeldung abgewiesen',
  'login.rate_limited': 'Ausgesperrt (zu viele Versuche)',
  'login.lockout_cleared': 'Sperre aufgehoben',
  'user.blocked': 'Konto gesperrt',
  'user.unblocked': 'Konto entsperrt',
  'password.changed': 'Passwort geändert',
  '2fa.totp_enabled': '2FA aktiviert',
  '2fa.totp_disabled': '2FA deaktiviert',
  '2fa.webauthn_registered': 'Passkey registriert',
  '2fa.webauthn_removed': 'Passkey entfernt',
  '2fa.backup_code_used': 'Mit Ersatzcode angemeldet',
  '2fa.backup_codes_regenerated': 'Ersatzcodes neu erzeugt',
  'user.2fa_reset': '2FA vom Betrieb zurückgesetzt',
  'user.created': 'Konto angelegt',
  'user.invited': 'Konto eingeladen',
  'user.invite_resent': 'Einladung erneut gesendet',
  'user.updated': 'Konto bearbeitet',
  'user.deleted': 'Konto gelöscht',
  'user.password_reset': 'Passwort zurückgesetzt',
  'user.roles.update': 'Rollen geändert',
  'tree.create': 'Stammbaum angelegt',
  'tree.update': 'Stammbaum bearbeitet',
  'tree.delete': 'Stammbaum gelöscht',
  'tree.owner_set': 'Besitzer gesetzt',
  'tree.transfer': 'Stammbaum übergeben',
  'tree.operator_access.granted': 'Betriebszugang genommen',
  'tree.operator_access.revoked': 'Betriebszugang beendet',
  'member.add': 'Mitglied aufgenommen',
  'member.remove': 'Mitglied entfernt',
  'member.role': 'Mitgliedsrolle geändert',
  'plan.create': 'Tarif angelegt',
  'plan.update': 'Tarif bearbeitet',
  'plan.delete': 'Tarif gelöscht',
  'plan.set_default': 'Vorgabe-Tarif geändert',
  'role.create': 'Rollenvorlage angelegt',
  'role.update': 'Rollenvorlage bearbeitet',
  'role.delete': 'Rollenvorlage gelöscht',
  'settings.updated': 'Einstellungen geändert',
  'texts.updated': 'Texte geändert',
  'smtp.updated': 'E-Mail-Einstellungen geändert',
  'medical.consent.grant': 'Medizin-Zugang erteilt',
  'medical.consent.revoke': 'Medizin-Zugang entzogen',
  'forum.eingeschaltet': 'Forum eingeschaltet',
  'forum.ausgeschaltet': 'Forum abgeschaltet',
  'forum.kategorie.neu': 'Forum: Bereich angelegt',
  'forum.kategorie.geaendert': 'Forum: Bereich geändert',
  'forum.meldung.erledigt': 'Forum: Meldung erledigt',
  'forum.beitrag.ausgeblendet': 'Forum: Beitrag ausgeblendet',
  'forum.beitrag.sichtbar': 'Forum: Beitrag eingeblendet',
  'forum.thema.geaendert': 'Forum: Thema geändert',
  'forum.sperre': 'Forum: Schreibsperre',
  'forum.sperre.aufgehoben': 'Forum: Sperre aufgehoben',
};

const ACTION_COLORS: Record<string, string> = {
  'login.success': 'bg-green-100 text-green-700',
  'login.failed': 'bg-red-100 text-red-700',
  'login.blocked': 'bg-red-100 text-red-700',
  'login.rate_limited': 'bg-red-100 text-red-700',
  'user.blocked': 'bg-red-100 text-red-700',
  'tree.delete': 'bg-red-100 text-red-700',
  'user.deleted': 'bg-red-100 text-red-700',
  'plan.delete': 'bg-red-100 text-red-700',
  'role.delete': 'bg-red-100 text-red-700',
  'medical.consent.revoke': 'bg-amber-100 text-amber-700',
  'medical.consent.grant': 'bg-amber-100 text-amber-700',
  'tree.operator_access.granted': 'bg-amber-100 text-amber-700',
  'forum.ausgeschaltet': 'bg-red-100 text-red-700',
  'forum.sperre': 'bg-amber-100 text-amber-700',
  'forum.beitrag.ausgeblendet': 'bg-amber-100 text-amber-700',
};
const DEFAULT_COLOR = 'bg-blue-100 text-blue-700';

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function ActionBadge({ action }: { action: string }) {
  const color = ACTION_COLORS[action] ?? DEFAULT_COLOR;
  const label = ACTION_LABELS[action] ?? action;
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function LogRow({ log }: { log: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
        onClick={() => log.details && setExpanded(!expanded)}
      >
        <td className="px-4 py-2.5 text-sm text-slate-500 whitespace-nowrap">
          {formatDate(log.createdAt)}
        </td>
        <td className="px-4 py-2.5">
          <ActionBadge action={log.action} />
        </td>
        <td className="px-4 py-2.5 text-sm text-slate-700">
          {log.userEmail ?? <span className="text-slate-400">System</span>}
        </td>
        <td className="px-4 py-2.5 text-sm text-slate-500 max-w-[200px] truncate">
          {log.target ?? '–'}
        </td>
        <td className="px-4 py-2.5 text-sm text-slate-400 font-mono text-xs">
          {log.ipAddress ?? '–'}
        </td>
        <td className="px-4 py-2.5 text-center">
          {log.details ? (
            <svg
              className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          ) : null}
        </td>
      </tr>
      {expanded && log.details && (
        <tr className="border-b border-slate-100 bg-slate-50">
          <td colSpan={6} className="px-4 py-3">
            <pre className="text-xs text-slate-600 whitespace-pre-wrap font-mono">
              {typeof log.details === 'string'
                ? (() => { try { return JSON.stringify(JSON.parse(log.details), null, 2); } catch { return log.details; } })()
                : JSON.stringify(log.details, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function LogCard({ log }: { log: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const detailsStr = log.details
    ? typeof log.details === 'string'
      ? (() => { try { return JSON.stringify(JSON.parse(log.details), null, 2); } catch { return log.details; } })()
      : JSON.stringify(log.details, null, 2)
    : null;

  return (
    <div
      className={`rounded-lg border border-slate-200 bg-white p-3 shadow-sm ${log.details ? 'cursor-pointer' : ''}`}
      onClick={() => log.details && setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between gap-2">
        <ActionBadge action={log.action} />
        <span className="shrink-0 text-xs text-slate-400">{formatDate(log.createdAt)}</span>
      </div>
      <p className="mt-2 truncate text-sm text-slate-700">
        {log.userEmail ?? <span className="text-slate-400">System</span>}
      </p>
      {log.target && (
        <p className="truncate text-xs text-slate-500">{log.target}</p>
      )}
      {log.ipAddress && (
        <p className="mt-1 font-mono text-xs text-slate-400">{log.ipAddress}</p>
      )}
      {expanded && detailsStr && (
        <pre className="mt-2 overflow-x-auto rounded-md bg-slate-50 p-2 text-xs text-slate-600 font-mono whitespace-pre-wrap">
          {detailsStr}
        </pre>
      )}
    </div>
  );
}

const LEER: ProtokollFilter = { action: '', email: '', from: '', to: '' };

export function AdminAuditLogPage() {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<ProtokollFilter>(LEER);
  const [laedtExport, setLaedtExport] = useState(false);
  const limit = 50;

  // Jede Filteränderung setzt zurück auf Seite 1: sonst stünde man nach dem
  // Einengen auf Seite 7 einer dreiseitigen Liste und sähe nichts.
  const setze = (teil: Partial<ProtokollFilter>) => {
    setFilter((f) => ({ ...f, ...teil }));
    setPage(1);
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['audit-log', page, filter],
    queryFn: () => getAuditLogs({ ...filter, page, limit }),
    placeholderData: keepPreviousData,
  });

  const totalPages = data?.pagination.totalPages ?? 1;
  const actions = Object.keys(ACTION_LABELS);
  const gefiltert = Object.values(filter).some((v) => (v ?? '') !== '');

  const exportieren = async () => {
    setLaedtExport(true);
    try {
      // Exportiert wird der gefilterte Bestand, nicht die sichtbare Seite –
      // sonst hätte man 50 Zeilen in der Datei und 8000 im Protokoll.
      await exportAuditLogs(filter);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export fehlgeschlagen.');
    } finally {
      setLaedtExport(false);
    }
  };

  const feld =
    'w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100';

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="shrink-0 border-b border-slate-200 bg-white px-4 py-2.5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-base font-semibold text-slate-800 sm:text-lg">Aktivitätsprotokoll</h1>
          <div className="flex items-center gap-2">
            {data && (
              <span className="text-xs text-slate-400">
                {data.pagination.total.toLocaleString('de-DE')} Einträge
              </span>
            )}
            <button
              onClick={exportieren}
              disabled={laedtExport}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              {laedtExport ? 'Wird erstellt…' : 'CSV'}
            </button>
          </div>
        </div>

        {/* Filter: am Handy untereinander, ab sm nebeneinander. */}
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <select
            aria-label="Aktion"
            value={filter.action ?? ''}
            onChange={(e) => setze({ action: e.target.value })}
            className={feld}
          >
            <option value="">Alle Aktionen</option>
            {actions.map((a) => (
              <option key={a} value={a}>{ACTION_LABELS[a]}</option>
            ))}
          </select>

          <input
            aria-label="Person"
            type="search"
            placeholder="Person (E-Mail)"
            value={filter.email ?? ''}
            onChange={(e) => setze({ email: e.target.value })}
            className={feld}
          />

          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            <span className="shrink-0">von</span>
            <input
              type="date"
              value={filter.from ?? ''}
              onChange={(e) => setze({ from: e.target.value })}
              className={feld}
            />
          </label>

          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            <span className="shrink-0">bis</span>
            <input
              type="date"
              value={filter.to ?? ''}
              onChange={(e) => setze({ to: e.target.value })}
              className={feld}
            />
          </label>

          <button
            onClick={() => { setFilter(LEER); setPage(1); }}
            disabled={!gefiltert}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            <FilterX className="h-4 w-4" aria-hidden="true" />
            Zurücksetzen
          </button>
        </div>
      </header>

      {/* Inhalt */}
      <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        {/* Lade/Fehler/Leer-Zustände */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
          </div>
        )}
        {isError && (
          <div className="py-12 text-center text-sm text-red-500">
            Fehler beim Laden des Protokolls.
          </div>
        )}
        {data && data.logs.length === 0 && (
          <div className="py-12 text-center text-sm text-slate-400">
            Keine Einträge gefunden.
          </div>
        )}

        {/* Desktop: Tabelle */}
        {data && data.logs.length > 0 && (
          <div className="hidden overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm md:block">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Zeitpunkt</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Aktion</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Benutzer</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Ziel</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-slate-500">IP</th>
                    <th className="px-4 py-2.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.logs.map((log) => (
                    <LogRow key={log.id} log={log} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Mobile: Karten */}
        {data && data.logs.length > 0 && (
          <div className="space-y-3 md:hidden">
            {data.logs.map((log) => (
              <LogCard key={log.id} log={log} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {data && totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-sm text-slate-500">
              Seite {page} von {totalPages}
            </p>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                &laquo;
              </button>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                &lsaquo;
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                &rsaquo;
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
